import type { ReactNode } from 'react';
import { FORM_KEY, type FormKey } from '@publishing/modules/forms/_types';

// ── 소분류 키: FORM_KEY를 그대로 alias
//    (category에는 PROMOTION, AFFILIATE를 허용하지 않으므로 "타입"에서만 제외)
export const SUB = FORM_KEY;

export type CategoryKey = Exclude<
  FormKey,
  typeof FORM_KEY.PROMOTION | typeof FORM_KEY.AFFILIATE
>;

/** 슬라이드 배너 텍스트 색 (미지정 시 white) */
export type SlideColorType = 'black' | 'white';

/** 상태 계산·기간 표시용 DateRange */
export interface DateRange {
  start: string;
  end?: string;
}

/** start 필수 period — pending / auto 윈도우 */
export type SchedulePeriod = {
  start: string;
  end?: string;
};

export interface BaseSchedule {
  period?: DateRange;
  recruitment?: DateRange;
}

/** compact — start/end + cycle(반복) + pending(1회성 예약) */
export interface CompactHubSchedule {
  start: string;
  end?: string;
  recruitment?: DateRange;
  /** n일 또는 KST 매월 — schedule.start/end 윈도우만 (pending과 무관) */
  cycle?: PendingScheduleCycle;
  /** 1회성 base 대체 — cycle과 무관, 활성 시 cycle보다 우선 */
  pending?: {
    start: string;
    end?: string;
    recruitment?: DateRange;
  };
}

export type HubEventSchedule = BaseSchedule | CompactHubSchedule;

/** n일 고정 주기 또는 KST 달력 월(1일~말일) */
export type PendingScheduleCycle = number | 'month';

/**
 * base schedule 대체 구간 — period 윈도우 안에서 활성
 * - cycle 없음: period 그대로 (1회성, end 없으면 상시)
 * - cycle 있음: period 윈도우 안에서 cycle 단위 period 자동 갱신 (end 있으면 그날까지 반복)
 */
export interface PendingSchedule {
  period: SchedulePeriod;
  cycle?: PendingScheduleCycle;
  /** pending 구간 활성 시 schedule.recruitment 대신 사용 */
  recruitment?: DateRange;
}

export type EventSchedule = {
  schedule: HubEventSchedule;
  pendingSchedule?: PendingSchedule;
};

export type ResolvedSchedule = {
  period?: DateRange;
  recruitment?: DateRange;
  source: 'auto' | 'pending' | 'base';
};

/**
 * schedule 작성 규칙
 * - period.start만 → 상시 (always)
 * - period.start + end → 해당 기간 진행 중 (live / soon)
 * - recruitment → 해당 기간 모집 중 (recruiting / soon)
 * - period + recruitment 동시 가능 → 기간이 겹치면 진행 중 + 모집 중 뱃지
 * - recruitment만 있는 이벤트 허용 (모집 종료 시 숨김)
 * - recruitment.end는 필수, period.end는 선택
 *
 * HUB_EVENTS 작성 규칙
 * - id: 이벤트 고유 식별자 (React key·내부 구분용)
 * - category: 탭 필터 카테고리 (SUB.*)
 * - href / target: 이동 URL (_blank는 target만 지정)
 * - imageKey: events/hub/banner_{key}.jpg, banner_{key}_mo.jpg, card_{key}.jpg
 * - schedule: start/end + cycle(반복) + pending(1회 예약)
 * - slideTitle / cardTitle / slideColor: 문구·색상 (이미지는 imageKey로 자동 연결)
 * - slideTitle과 cardTitle 중 하나 이상 필수
 * - cardTitle: 카드 문구이자 슬라이드 GTM btnname (slideTitle은 JSX라 GA 값으로 쓸 수 없음)
 */
export type HubEvent = {
  id: string;
  /** 수동 노출 순번. 1부터 오름차순 고정. 미지정 시 schedule 기준 자동 정렬 */
  order?: number;
  category: CategoryKey;
  href: string;
  target?: '_blank';
  imageKey: string;
  memo?: string;
  soonDays?: number;
  /** 슬라이드 배너 문구 (JSX 허용) */
  slideTitle?: ReactNode;
  /** 슬라이드 배너 텍스트 색 — 미지정 시 white */
  slideColor?: SlideColorType;
  /** 카드 배너 문구 · 슬라이드 GTM btnname */
  cardTitle?: string;
} & EventSchedule;
